import { cx } from '@twind/core';
import { Body } from 'app/components/Typography';
import { ButtonType, ChatModuleType, LiveChatModule } from 'app/copied/models/ChatModules';
import { type LiveChatItem } from 'app/hooks/fetch/useFetchLiveChat';
import useAutoOpenDelay from 'app/hooks/useAutoOpenDelay';
import useHTMLLinkClickHandler from 'app/hooks/useHTMLLinkClickHandler';
import useModule from 'app/hooks/useModule';
import { getButtonClass } from 'app/utils/getButtonClass';
import { linkify } from 'app/utils/linkify';
import { FormEvent, useCallback, useEffect, useState } from 'react';

import useLiveChatTracking from '../../hooks/useLiveChatTracking';

type Props = {
  data?: LiveChatItem;
  onSubmit: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  showCheckbox?: boolean;
  buttonType?: ButtonType;
  isLoading?: boolean;
};

const LiveChatConsentForm = ({ data, onSubmit, showCheckbox, isLoading }: Props) => {
  const [checked, setChecked] = useState(false);
  const handleHTMLLinkClick = useHTMLLinkClickHandler();
  const { captureStartScreen, captureTermsChecked } = useLiveChatTracking();
  const liveChatModule = useModule(ChatModuleType.LiveChat) as unknown as LiveChatModule;
  const autoOpenDelay = useAutoOpenDelay();

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const formData = new FormData(event.target as HTMLFormElement);
      const json = Object.fromEntries(formData);
      const modified = {
        ...json,
        value: json.value === 'on',
      };
      onSubmit(modified);
    },
    [onSubmit]
  );

  useEffect(() => {
    captureStartScreen(autoOpenDelay);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;

  return (
    <form
      id="ostro-livechat-consent-form"
      onSubmit={handleSubmit}
      className="flex flex-col items-center gap-4"
    >
      <input
        type="hidden"
        name="form_ref_id"
        value={data.ref_id}
      />
      {data.items?.map((item, index) => {
        switch (item.item_type) {
          case 'checkbox':
            return (
              <div
                key={index}
                className="flex gap-4"
              >
                <input
                  type="hidden"
                  name="item_ref_id"
                  value={item.ref_id}
                />
                {showCheckbox && (
                  <input
                    className="shrink-0 w-5 h-5 accent-[#1e1e1e] border border-[#1e1e1e]"
                    name="value"
                    type="checkbox"
                    id={`T&P-accept-${item.ref_id}`}
                    checked={checked}
                    onChange={() => {
                      captureTermsChecked(!checked);
                      setChecked(!checked);
                    }}
                  />
                )}
                <div className="flex flex-col">
                  <label
                    className="text-sm cursor-pointer [&_a]:underline [&_h1]:(text-xl leading-6 font-semibold) [&_p]:(mb-2 empty:pb-[1em] last-of-type:mb-0)"
                    htmlFor={`T&P-accept-${item.ref_id}`}
                    onClick={handleHTMLLinkClick}
                    dangerouslySetInnerHTML={{
                      __html: linkify(item.params.label),
                    }}
                  />
                  {showCheckbox && !checked && item.errors
                    ? item.errors.map((error: string) => (
                        <Body
                          key={error}
                          className={cx('ostro-livechat-consent-form-input-error', 'text-error')}
                          role="alert"
                        >
                          {error}
                        </Body>
                      ))
                    : null}
                </div>
              </div>
            );
          default:
            return null;
        }
      })}
      <button
        id="ostro-livechat-consent-form-submit"
        disabled={isLoading}
        className={cx('w-full', getButtonClass(liveChatModule?.button_type))}
        type="submit"
      >
        {data.form_parameters?.submit_text}
      </button>
    </form>
  );
};

export default LiveChatConsentForm;
